import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import {
  FilterList,
  ViewList,
  PhoneAndroid,
  HourglassEmpty,
  Language,
  Business,
  Storefront,
} from '@mui/icons-material';
import { colors } from '../theme/theme';
import { TabId } from './leadsInquiries/types';
import { fetchAllLeadCounts } from './leadsInquiries/fetchCounts';
import { PAGE_TAB_GROUP_SX } from './leadsInquiries/shared';
import { AllTab } from './leadsInquiries/AllTab';
import { BookingLeadsTab } from './leadsInquiries/BookingLeadsTab';
import { WaitlistTab } from './leadsInquiries/WaitlistTab';
import { WebsiteInquiriesTab } from './leadsInquiries/WebsiteInquiriesTab';
import { CorporateTab } from './leadsInquiries/CorporateTab';
import { StudioContactTab } from './leadsInquiries/StudioContactTab';

const TABS: { id: TabId; label: string; icon: React.ReactNode; countKey?: keyof typeof EMPTY_COUNTS }[] = [
  { id: 'all', label: 'All', icon: <ViewList sx={{ fontSize: 20 }} /> },
  { id: 'booking', label: 'Booking Leads', icon: <PhoneAndroid sx={{ fontSize: 20 }} />, countKey: 'booking' },
  { id: 'waitlist', label: 'Waitlist', icon: <HourglassEmpty sx={{ fontSize: 20 }} />, countKey: 'waitlist' },
  { id: 'website', label: 'Website Inquiries', icon: <Language sx={{ fontSize: 20 }} />, countKey: 'website' },
  { id: 'corporate', label: 'Corporate', icon: <Business sx={{ fontSize: 20 }} />, countKey: 'corporate' },
  { id: 'studioContact', label: 'Studio Contact', icon: <Storefront sx={{ fontSize: 20 }} />, countKey: 'studioContact' },
];

const EMPTY_COUNTS = { booking: 0, waitlist: 0, website: 0, corporate: 0, studioContact: 0, total: 0, bookingThisMonth: 0 };

const LeadsInquiriesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(() => new Set<TabId>(['all']));
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [countsLoading, setCountsLoading] = useState(true);
  const [countsError, setCountsError] = useState<string | null>(null);

  const loadCounts = useCallback(async () => {
    try {
      setCountsError(null);
      const data = await fetchAllLeadCounts();
      setCounts(data);
    } catch (err: unknown) {
      setCountsError(err instanceof Error ? err.message : 'Failed to load contact counts');
    } finally {
      setCountsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const handleTabChange = (_: React.MouseEvent<HTMLElement>, next: TabId | null) => {
    if (!next) return;
    setActiveTab(next);
    setVisitedTabs((prev) => new Set(prev).add(next));
  };

  const handleNavigateFromAll = (tab: Exclude<TabId, 'all'>) => {
    setActiveTab(tab);
    setVisitedTabs((prev) => new Set(prev).add(tab));
  };

  const tabLabel = (tab: typeof TABS[number]) => {
    if (tab.id === 'all') {
      return countsLoading ? tab.label : `${tab.label} (${counts.total.toLocaleString()})`;
    }
    if (tab.countKey && !countsLoading) {
      return `${tab.label} (${counts[tab.countKey].toLocaleString()})`;
    }
    return tab.label;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={3}
        mb={4}
        sx={{ width: '100%' }}
      >
        <Box display="flex" gap={3} alignItems="center" flex={1} minWidth={300}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: alpha(colors.primary, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FilterList sx={{ fontSize: 28, color: colors.primary }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              color={colors.textPrimary}
              mb={1}
              lineHeight={1.25}
            >
              Leads &amp; Inquiries
            </Typography>
            <Typography
              variant="body1"
              color={colors.textSecondary}
              lineHeight={1.6}
            >
              Every inbound contact in one place — booking leads, waitlists, website join requests and corporate enquiries.
            </Typography>
          </Box>
        </Box>
        <Box
          textAlign="right"
          flexShrink={0}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: `1px solid ${colors.divider}`,
            backgroundColor: 'white',
            minWidth: 120,
          }}
        >
          {countsLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography variant="h4" fontWeight={700} color={colors.textPrimary} lineHeight={1.2}>
                {counts.total.toLocaleString()}
              </Typography>
              <Typography variant="body2" color={colors.textSecondary} mt={0.5}>
                total contacts
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {countsError && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setCountsError(null)}>
          {countsError}
        </Alert>
      )}

      <ToggleButtonGroup
        exclusive
        value={activeTab}
        onChange={handleTabChange}
        sx={PAGE_TAB_GROUP_SX}
      >
        {TABS.map((tab) => (
          <ToggleButton key={tab.id} value={tab.id}>
            {tab.icon}
            {tabLabel(tab)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {visitedTabs.has('all') && (
        <Box display={activeTab === 'all' ? 'block' : 'none'} sx={{ width: '100%' }}>
          <AllTab counts={counts} onNavigate={handleNavigateFromAll} />
        </Box>
      )}
      {visitedTabs.has('booking') && (
        <Box display={activeTab === 'booking' ? 'block' : 'none'} sx={{ width: '100%' }}>
          <BookingLeadsTab />
        </Box>
      )}
      {visitedTabs.has('waitlist') && (
        <Box display={activeTab === 'waitlist' ? 'block' : 'none'} sx={{ width: '100%' }}>
          <WaitlistTab />
        </Box>
      )}
      {visitedTabs.has('website') && (
        <Box display={activeTab === 'website' ? 'block' : 'none'} sx={{ width: '100%' }}>
          <WebsiteInquiriesTab />
        </Box>
      )}
      {visitedTabs.has('corporate') && (
        <Box display={activeTab === 'corporate' ? 'block' : 'none'} sx={{ width: '100%' }}>
          <CorporateTab />
        </Box>
      )}
      {visitedTabs.has('studioContact') && (
        <Box display={activeTab === 'studioContact' ? 'block' : 'none'} sx={{ width: '100%' }}>
          <StudioContactTab />
        </Box>
      )}
    </Box>
  );
};

export default LeadsInquiriesPage;
